import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { useReportLabCriterion } from '../../hooks/lab-hooks';
import {
  LabCriterionState,
  LabRun,
  LabRunCriterion,
  labCriterionStateSentiment,
} from '../../support/labService';
import { LabDate, LabJsonView, LabStatusTag, labErrorMessage } from './LabShared';

export type LabRunCriteriaPanelProps = {
  run: LabRun;
  criteria: LabRunCriterion[];
  /** Reporting and waiving are proctor actions; hide them for a learner looking at their own run. */
  canReport?: boolean;
};

type ReportTarget = {
  criterion: LabRunCriterion;
  mode: 'report' | 'waive';
};

/**
 * How the run is being scored.
 *
 * Two verbs, and they are not the same thing. *Reporting* records the verdict for a criterion
 * whose evaluator cannot derive one - a proctor watched somebody do it. *Waiving* says the
 * criterion does not apply to this run, and is recorded with who did it and why, because
 * "this one did not count" is exactly the claim that needs a name against it.
 */
const LabRunCriteriaPanel: React.FC<LabRunCriteriaPanelProps> = ({
  run,
  criteria,
  canReport = false,
}) => {
  const [target, setTarget] = useState<ReportTarget | null>(null);
  const [state, setState] = useState<LabCriterionState>('met');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const report = useReportLabCriterion();

  const openReport = (criterion: LabRunCriterion, mode: 'report' | 'waive') => {
    setTarget({ criterion, mode });
    setState(mode === 'waive' ? 'waived' : 'met');
    setReason('');
    setFormError(null);
    report.reset();
  };

  const handleSubmit = async () => {
    if (!target) return;

    if (target.mode === 'waive' && !reason.trim()) {
      setFormError('A waiver needs a reason. It is recorded against your name.');
      return;
    }

    setFormError(null);

    try {
      await report.mutateAsync({
        runId: run.id,
        criterionKey: target.criterion.criterion_key,
        ...(target.mode === 'waive'
          ? { waive: true, reason: reason.trim() }
          : { state, ...(reason.trim() ? { reason: reason.trim() } : {}) }),
      });
      setTarget(null);
    } catch {
      // Surfaced through report.error.
    }
  };

  if (!criteria.length) {
    return (
      <p className="m-0 text-sm text-gray-600">
        This run has no criteria, so nothing scores it.
      </p>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2 font-semibold">Criterion</th>
              <th className="px-3 py-2 font-semibold">State</th>
              <th className="px-3 py-2 font-semibold">Weight</th>
              <th className="px-3 py-2 font-semibold">Evaluated</th>
              {canReport && <th className="px-3 py-2 font-semibold text-right">Actions</th>}
            </tr>
          </thead>

          <tbody>
            {criteria.map((criterion) => (
              <tr key={criterion.id} className="border-b border-gray-100 align-top">
                <td className="px-3 py-2">
                  <div className="font-medium">{criterion.criterion_key}</div>
                  <div className="text-xs text-gray-500">{criterion.criterion_name}</div>

                  <div className="mt-1 flex flex-wrap gap-1">
                    {criterion.required && <LabStatusTag label="Required" />}
                  </div>

                  {criterion.state === 'waived' && (
                    <div className="mt-1 text-xs text-amber-800">
                      {`Waived by ${criterion.waived_by_email || 'unknown'}`}
                      {criterion.waive_reason ? `: ${criterion.waive_reason}` : ''}
                    </div>
                  )}

                  {criterion.detail && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-xs text-gray-500">Detail</summary>
                      <LabJsonView value={criterion.detail} />
                    </details>
                  )}
                </td>

                <td className="px-3 py-2 whitespace-nowrap">
                  <LabStatusTag
                    label={criterion.state}
                    sentiment={labCriterionStateSentiment(criterion.state)}
                  />
                  {criterion.first_met_at && criterion.state !== 'met' && (
                    <div className="text-xs text-gray-500">
                      was met earlier, then stopped being true
                    </div>
                  )}
                </td>

                <td className="px-3 py-2 whitespace-nowrap">{criterion.weight}</td>

                <td className="px-3 py-2 whitespace-nowrap">
                  <LabDate value={criterion.evaluated_at} />
                </td>

                {canReport && (
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <StbButton
                        role="update"
                        size="xsmall"
                        onClick={async () => openReport(criterion, 'report')}
                      >
                        Report
                      </StbButton>
                      {criterion.state !== 'waived' && (
                        <StbButton
                          role="deny"
                          size="xsmall"
                          onClick={async () => openReport(criterion, 'waive')}
                        >
                          Waive
                        </StbButton>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <StbModal
        open={!!target}
        role="update"
        title={
          target?.mode === 'waive'
            ? `Waive ${target?.criterion.criterion_key}`
            : `Report ${target?.criterion.criterion_key}`
        }
        submitLabel={target?.mode === 'waive' ? 'Waive it' : 'Record the verdict'}
        suppressCloseOnSubmit
        onSubmit={handleSubmit}
        onClose={async () => setTarget(null)}
        apiResponseSection={
          formError || report.isError ? (
            <p className="m-0 text-sm text-amber-800">
              {formError || labErrorMessage(report.error)}
            </p>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-4">
          {target?.mode === 'report' ? (
            <>
              <p className="m-0 text-sm text-gray-600">
                {`Record the verdict for "${target.criterion.criterion_name}". For a manual or
                externally reported criterion this is the only place the answer comes from.`}
              </p>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-gray-700">Verdict</span>
                <select
                  className="rounded border border-gray-300 p-2 text-sm"
                  value={state}
                  onChange={(evt) => setState(evt.target.value as LabCriterionState)}
                >
                  <option value="met">Met</option>
                  <option value="unmet">Not met</option>
                  <option value="pending">Back to pending</option>
                  <option value="error">Error</option>
                </select>
              </label>
            </>
          ) : (
            <p className="m-0 text-sm text-gray-600">
              Waiving takes this criterion out of the score for this run only. Your address and
              this reason are recorded against it.
            </p>
          )}

          <TextField
            label={target?.mode === 'waive' ? 'Reason' : 'Note (optional)'}
            value={reason}
            onChange={(evt) => setReason(evt.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </div>
      </StbModal>
    </>
  );
};

export default LabRunCriteriaPanel;
